'use client';
import { useState, useEffect, useRef, type ReactNode, type MouseEvent as ReactMouseEvent, type SyntheticEvent } from 'react';
import {
  Plus, Search, Upload, X, GripVertical, Play, Zap, Loader2,
  Download, Timer, HardDrive, Trash2, ImageIcon, Check,
  RotateCcw, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Clock, Eye,
  Calendar, Share2, FileJson, Trophy, Instagram, Facebook,
} from 'lucide-react';
import type {
  ImageVideo, LibraryImage, ImagnImage, TextOverlay, VideoSize,
  OverlayPosition, WordBreaking, FontSize, WorldCupRun,
  WorldCupPreviewMatch, WorldCupReactionMatch, GoalScorer, GoalScorers, MusicChoice,
} from '@/lib/types';
import { flagPathForTeam } from '@/lib/worldcup-flags';
import MusicPicker, { WORLDCUP_DEFAULT_TRACK, nameForTrackUrl } from '@/components/MusicPicker';

const EMPTY_SCORERS: GoalScorers = { home: [], away: [] };

// World Cup videos default to the bundled epic-cinematic track, music on.
const WC_DEFAULT_MUSIC: MusicChoice = { enabled: true, url: WORLDCUP_DEFAULT_TRACK.url, name: WORLDCUP_DEFAULT_TRACK.name };

// Seed the per-row music edit state from the stored columns.
const musicFromVideo = (v: ImageVideo): MusicChoice => ({
  enabled: !!v.music_enabled,
  url: v.music_url ?? null,
  name: nameForTrackUrl(v.music_url),
});

// Editable scoreboard props in the expanded-row detail view (World Cup only).
type ScoreboardEdit = {
  team1: string; team2: string; score: string;
  goalScorers: GoalScorers;
  showScore: boolean; showFlags: boolean; showGoalScorers: boolean;
};

// Editable fields in the expanded-row detail view (re-generate applies these).
type RowEdit = { script: string; videoSize: VideoSize; overlay: TextOverlay; music: MusicChoice; scoreboard: ScoreboardEdit };

// Seed the per-row scoreboard edit state from the stored columns.
const scoreboardFromVideo = (v: ImageVideo): ScoreboardEdit => ({
  team1: v.team1 ?? '',
  team2: v.team2 ?? '',
  score: v.score ?? '',
  goalScorers: normalizeScorers(v.goal_scorers),
  showScore: v.show_score ?? true,
  showFlags: v.show_flags ?? true,
  showGoalScorers: v.show_goal_scorers ?? true,
});

// Coerce whatever the reaction prompt returned for `goal_scorers` into the
// strict { home: GoalScorer[]; away: GoalScorer[] } shape — the model can omit a
// side, hand back strings, or add stray fields, and none of that should break.
const normalizeScorers = (raw: unknown): GoalScorers => {
  const side = (arr: unknown): GoalScorer[] =>
    Array.isArray(arr)
      ? arr.map((e): GoalScorer => {
          if (typeof e === 'string') return { player: e, minute: '' };
          const o = (e ?? {}) as Record<string, unknown>;
          return { player: String(o.player ?? ''), minute: String(o.minute ?? '') };
        }).filter((s) => s.player.trim())
      : [];
  const r = (raw ?? {}) as Record<string, unknown>;
  return { home: side(r.home), away: side(r.away) };
};

type GenStage = 'idle' | 'preparing' | 'audio' | 'rendering' | 'done' | 'error';
interface GenState {
  stage: GenStage;
  progress: number;
  videoUrl?: string;
  fileSizeBytes?: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

type PickerSource = 'imagn' | 'upload' | 'library';

// A chosen image during composition (see ImagesTab for the same model). Nothing
// is downloaded from Imagn or uploaded to S3 until the user clicks Generate.
interface ChosenImage {
  key: string;
  previewUrl: string;
  s3_url?: string;
  imagnId?: string;
  file?: File;
  caption?: string;
}

const VIDEO_SIZES: VideoSize[] = ['9:16', '16:9', '1:1', '4:5'];
const POSITIONS: OverlayPosition[] = ['top', 'center', 'bottom'];
const WORD_BREAKING: WordBreaking[] = ['one', 'few', 'several'];
const FONT_SIZES: FontSize[] = ['small', 'medium', 'big'];

const IMAGN_FETCH_LIMIT = 50;
const IMAGN_PER_PAGE = 24;
const IMAGN_MAX_RESULTS = 300;

const DEFAULT_OVERLAY: TextOverlay = {
  position: 'center',
  wordBreaking: 'one',
  size: 'big',
  color: '#FFFFFF',
};

// Today's date (YYYY-MM-DD) in the America/New_York (ET) timezone.
const etToday = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());

const shiftDate = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const prettyDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

// Merge a run's preview + reaction matches into one row per matchup (keyed by a
// normalized teams string), so a matchup can offer up to four script chips.
interface Matchup {
  teams: string;
  preview?: WorldCupPreviewMatch;
  reaction?: WorldCupReactionMatch;
}
const mergeMatchups = (run: WorldCupRun): Matchup[] => {
  const norm = (s: string) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const map = new Map<string, Matchup>();
  (run.preview_json ?? []).forEach((m) => {
    const k = norm(m.teams);
    const e = map.get(k) ?? { teams: m.teams };
    e.preview = m;
    map.set(k, e);
  });
  (run.reaction_json ?? []).forEach((m) => {
    const k = norm(m.teams);
    const e = map.get(k) ?? { teams: m.teams };
    e.reaction = m;
    if ((m.teams?.length ?? 0) > (e.teams?.length ?? 0)) e.teams = m.teams;
    map.set(k, e);
  });
  return [...map.values()];
};

// Split a "Home vs Away" matchup label into [team1, team2] (handles "vs", "vs.",
// "v"). These feed the team1/team2 scoreboard props sent to the Remotion render.
const parseTeams = (teams: string): [string, string] => {
  const parts = (teams || '').split(/\s+vs?\.?\s+/i);
  return [(parts[0] ?? '').trim(), (parts[1] ?? '').trim()];
};

interface ScriptMeta { team1: string; team2: string; score: string; goalScorers: GoalScorers }

// Pre-game scripts (Preview / Betting Line) have no score yet, so they get no
// scoreboard. Everything else — Recap / Hot Take, or a manually-typed script with
// no chip selected — shows one by default. The script type is the last segment of
// the selection key (e.g. "latest:0:hot", "det:<id>:2:preview").
const PREGAME_SCRIPT_TYPES = new Set(['preview', 'betting']);
const scriptHasScoreboard = (key: string | null): boolean =>
  !key || !PREGAME_SCRIPT_TYPES.has(key.split(':').pop() ?? '');

// Whether an existing video carries scoreboard data — so re-generation shows the
// scoreboard editor only for the post-game videos that actually have one.
const videoHasScoreboard = (v: ImageVideo): boolean =>
  !!(v.team1?.trim() || v.team2?.trim() || v.score?.trim() ||
     (v.goal_scorers?.home?.length ?? 0) > 0 || (v.goal_scorers?.away?.length ?? 0) > 0);

const formatElapsed = (secs: number) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
};
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDuration = (secs: number) => {
  if (!isFinite(secs)) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

function RatioBox({ size }: { size: VideoSize }) {
  const dims: Record<VideoSize, [number, number]> = {
    '9:16': [9, 16], '16:9': [16, 9], '1:1': [1, 1], '4:5': [4, 5],
  };
  const [w, h] = dims[size] ?? [9, 16];
  const scale = 16 / Math.max(w, h);
  return (
    <span className="block border-[1.5px] border-current rounded-[2px]" style={{ width: `${w * scale}px`, height: `${h * scale}px` }} />
  );
}

function PositionGlyph({ position }: { position: OverlayPosition }) {
  const bar = position === 'top' ? 'top-[2px]' : position === 'center' ? 'top-1/2 -translate-y-1/2' : 'bottom-[2px]';
  return (
    <span className="relative block w-3.5 h-4 border-[1.5px] border-current rounded-[2px]">
      <span className={`absolute left-[2px] right-[2px] h-[2.5px] bg-current rounded-full ${bar}`} />
    </span>
  );
}

function FontGlyph({ size }: { size: FontSize }) {
  const px = size === 'small' ? 10 : size === 'medium' ? 14 : 18;
  return <span className="font-black leading-none" style={{ fontSize: `${px}px` }}>A</span>;
}

// Resolved flag for a team name (or a neutral placeholder when unrecognized) —
// gives immediate feedback that the team→flag mapping matched.
function FlagThumb({ name }: { name: string }) {
  const src = flagPathForTeam(name);
  if (!src) return <span className="w-6 h-[18px] shrink-0 rounded-[2px] bg-white/5 border border-white/10" title="No flag matched" />;
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" title={src} className="w-6 h-[18px] shrink-0 rounded-[2px] object-cover border border-white/10" />;
}

// On/off pill for a scoreboard element (Score / Flags / Goal scorers). The
// state rides along in the props so the Remotion composition can gate rendering.
function ToggleChip({ label, active, onChange }: { label: string; active: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!active)}
      role="switch"
      aria-checked={active}
      className={`inline-flex items-center gap-1.5 rounded-full pl-1 pr-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors border ${
        active ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'bg-white/[0.03] text-slate-500 border-white/10 hover:text-slate-300'
      }`}
    >
      <span className={`w-4 h-4 rounded-full flex items-center justify-center ${active ? 'bg-emerald-500 text-black' : 'bg-white/10'}`}>
        {active && <Check size={10} strokeWidth={3} />}
      </span>
      {label}
    </button>
  );
}

// Editable list of goal scorers for one side of the match. Auto-filled from the
// picked matchup; rows can be edited, added, or removed before generating.
function ScorerList({
  side, teamName, scorers, onChange,
}: {
  side: 'home' | 'away';
  teamName: string;
  scorers: GoalScorer[];
  onChange: (next: GoalScorer[]) => void;
}) {
  const update = (i: number, patch: Partial<GoalScorer>) =>
    onChange(scorers.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => onChange(scorers.filter((_, idx) => idx !== i));
  const add = () => onChange([...scorers, { player: '', minute: '' }]);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <FlagThumb name={teamName} />
        <span className="text-xs font-bold text-white truncate">{teamName || (side === 'home' ? 'Home' : 'Away')}</span>
      </div>
      {scorers.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={s.player} onChange={(e) => update(i, { player: e.target.value })} placeholder="Scorer"
            className="flex-1 min-w-0 bg-slate-900/50 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
          <input value={s.minute} onChange={(e) => update(i, { minute: e.target.value })} placeholder="23'"
            className="w-16 shrink-0 text-center bg-slate-900/50 border border-white/10 rounded-md px-1 py-1.5 text-xs font-mono text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
          <button onClick={() => remove(i)} title="Remove scorer"
            className="shrink-0 p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors"><X size={13} /></button>
        </div>
      ))}
      <button onClick={add}
        className="w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed border-white/15 hover:border-white/35 hover:bg-white/[0.03] text-slate-400 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors">
        <Plus size={12} /> Add scorer
      </button>
    </div>
  );
}

function StepHeader({ label, right }: { label: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap mb-3 min-h-[28px]">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-300">{label}</h4>
      {right}
    </div>
  );
}

function Segmented<T extends string>({
  options, value, onChange, labels, icon, compact,
}: {
  options: T[];
  value: T;
  onChange: (v: T) => void;
  labels?: Record<string, string>;
  icon?: (opt: T) => ReactNode;
  compact?: boolean;
}) {
  const layout = compact
    ? 'flex-row items-center gap-1.5 px-2.5 py-1'
    : icon
      ? 'flex-col items-center justify-center gap-1.5 w-[3.4rem] py-2'
      : 'flex-row items-center justify-center gap-1.5 px-3 py-1.5';
  return (
    <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={`flex rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border ${layout} ${
              active ? 'bg-white/[0.12] text-white border-white/20' : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/[0.04]'
            }`}
          >
            {icon && (
              <span className={`flex items-center justify-center ${compact ? '' : 'h-[18px]'} ${active ? 'text-white' : 'text-slate-500'}`}>
                {icon(opt)}
              </span>
            )}
            <span>{labels?.[opt] ?? opt}</span>
          </button>
        );
      })}
    </div>
  );
}

// A clickable image-search-query chip → feeds the Imagn search.
function QueryChip({ q, onClick }: { q: string; onClick: (q: string) => void }) {
  return (
    <button
      onClick={() => onClick(q)}
      title="Search Imagn for this"
      className="group inline-flex items-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1 text-[10px] font-medium transition-colors max-w-full"
    >
      <Search size={10} className="shrink-0 opacity-70" />
      <span className="truncate">{q}</span>
    </button>
  );
}

// One labelled script block with an optional "Use as script" action.
function ScriptBlock({ label, text, onUse }: { label: string; text?: string; onUse?: (text: string) => void }) {
  if (!text) return null;
  return (
    <div className="bg-black/30 rounded-lg border border-white/10 p-3">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</span>
        {onUse && (
          <button
            onClick={() => onUse(text)}
            className="flex items-center gap-1 bg-amber-500/15 hover:bg-amber-500 hover:text-black border border-amber-500/40 text-amber-300 rounded-md px-2 py-1 text-[9px] font-black uppercase tracking-wider transition-colors"
            title="Use this as the Audio Script"
          >
            <Zap size={10} fill="currentColor" /> Use as script
          </button>
        )}
      </div>
      <p className="text-[11px] leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}

// Compact badge showing a video's social-publish state (per-platform ticks).
function PublishBadge({ published, v }: { published: boolean; v: ImageVideo }) {
  if (published) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400 text-[9px] font-black uppercase tracking-widest" title={v.published_at ? `Published ${new Date(v.published_at).toLocaleString()}` : 'Published'}>
        <Check size={9} /> Published
        {v.ig_media_id && <Instagram size={10} className="text-pink-400" />}
        {v.fb_post_id && <Facebook size={10} className="text-sky-400" />}
      </span>
    );
  }
  if (v.publish_error && (v.publish_attempts ?? 0) > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600/20 text-red-400 text-[9px] font-black uppercase tracking-widest" title={v.publish_error}>
        <X size={9} /> Publish failed
      </span>
    );
  }
  return null;
}

// A quick-select chip for one script of a matchup (Preview / Hot take / …).
function ScriptChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Use this script + suggest its image searches"
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors border ${
        active
          ? 'bg-amber-500 text-black border-amber-400'
          : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30'
      }`}
    >
      <Zap size={10} fill="currentColor" /> {label}
    </button>
  );
}

// One compact row per matchup, with a clickable chip for each available script.
// Picking a chip propagates the script (and its image searches) to the generator.
function QuickScriptSelect({
  matchups, keyPrefix, activeKey, onPick,
}: {
  matchups: Matchup[];
  keyPrefix: string;
  activeKey: string | null;
  onPick: (text: string, queries: string[] | undefined, key: string, meta: ScriptMeta) => void;
}) {
  if (matchups.length === 0) return null;
  return (
    <div className="space-y-2">
      {matchups.map((mu, mi) => {
        const [team1, team2] = parseTeams(mu.teams);
        const meta: ScriptMeta = {
          team1, team2,
          score: mu.reaction?.verified_score ?? '',
          goalScorers: normalizeScorers(mu.reaction?.goal_scorers),
        };
        return (
        <div key={mi} className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
          <span className="text-xs font-bold text-white mr-1">{mu.teams}</span>
          {mu.preview?.preview_narrative_script && (
            <ScriptChip label="Preview" active={activeKey === `${keyPrefix}:${mi}:preview`}
              onClick={() => onPick(mu.preview!.preview_narrative_script!, mu.preview!.image_search_queries, `${keyPrefix}:${mi}:preview`, meta)} />
          )}
          {mu.reaction?.hot_take_high_temp && (
            <ScriptChip label="Hot take" active={activeKey === `${keyPrefix}:${mi}:hot`}
              onClick={() => onPick(mu.reaction!.hot_take_high_temp!, mu.reaction!.image_search_queries, `${keyPrefix}:${mi}:hot`, meta)} />
          )}
          {mu.preview?.betting_line_movement && (
            <ScriptChip label="Betting line" active={activeKey === `${keyPrefix}:${mi}:betting`}
              onClick={() => onPick(mu.preview!.betting_line_movement!, mu.preview!.image_search_queries, `${keyPrefix}:${mi}:betting`, meta)} />
          )}
          {mu.reaction?.recap_script_low_temp && (
            <ScriptChip label="Recap (Factual)" active={activeKey === `${keyPrefix}:${mi}:recap`}
              onClick={() => onPick(mu.reaction!.recap_script_low_temp!, mu.reaction!.image_search_queries, `${keyPrefix}:${mi}:recap`, meta)} />
          )}
        </div>
        );
      })}
    </div>
  );
}

export default function WorldCupTab({
  videos, onRefresh, runs, onRefreshRuns,
}: {
  videos: ImageVideo[];
  onRefresh: () => void;
  runs: WorldCupRun[];
  onRefreshRuns: () => void;
}) {
  const [showCreate, setShowCreate] = useState(false);

  // Creation form state.
  const [chosen, setChosen] = useState<ChosenImage[]>([]);
  const [script, setScript] = useState('');
  const [videoSize, setVideoSize] = useState<VideoSize>('9:16');
  const [overlay, setOverlay] = useState<TextOverlay>(DEFAULT_OVERLAY);
  const [music, setMusic] = useState<MusicChoice>(WC_DEFAULT_MUSIC); // default track, on

  // Scoreboard props sent to the Remotion render — auto-filled from the picked
  // matchup, editable below. team1/team2 drive the flag lookup server-side.
  const [team1, setTeam1] = useState('');
  const [team2, setTeam2] = useState('');
  const [score, setScore] = useState('');
  const [goalScorers, setGoalScorers] = useState<GoalScorers>(EMPTY_SCORERS);

  // Scoreboard display toggles — on by default, ride along in the render props.
  const [showScore, setShowScore] = useState(true);
  const [showFlags, setShowFlags] = useState(true);
  const [showGoalScorers, setShowGoalScorers] = useState(true);

  // Image-search suggestions tied to the currently-selected script — only the
  // queries that came with the chosen script, not the whole run.
  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [selectedScriptKey, setSelectedScriptKey] = useState<string | null>(null);

  // World Cup date + prompt-running state (replaces the "Enter Player or Topic"
  // research flow). Default to today in ET.
  const [date, setDate] = useState<string>(etToday());
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  // Image picker state.
  const [source, setSource] = useState<PickerSource>('imagn');
  const [imagnQuery, setImagnQuery] = useState('');
  const [imagnResults, setImagnResults] = useState<ImagnImage[]>([]);
  const [imagnSearching, setImagnSearching] = useState(false);
  const [imagnLoadingMore, setImagnLoadingMore] = useState(false);
  const [imagnPage, setImagnPage] = useState(0);
  const searchTokenRef = useRef(0);
  const [lastSearched, setLastSearched] = useState<string | null>(null); // last executed query, for the empty-state message
  const [library, setLibrary] = useState<LibraryImage[]>([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  const [viewing, setViewing] = useState<ImageVideo | null>(null);

  const [previewing, setPreviewing] = useState(false);
  const [gen, setGen] = useState<GenState>({ stage: 'idle', progress: 0 });
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragIndex = useRef<number | null>(null);
  const createRef = useRef<HTMLDivElement | null>(null);

  // Generated-outputs (runs) list state.
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [runView, setRunView] = useState<Record<string, 'both' | 'preview' | 'reaction'>>({});

  // Existing-videos list state.
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [rowGen, setRowGen] = useState<Record<string, { stage: 'rendering' | 'error'; progress: number }>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [sizes, setSizes] = useState<Record<string, number>>({});
  const [editState, setEditState] = useState<Record<string, RowEdit>>({});
  const [publishedById, setPublishedById] = useState<Record<string, boolean>>({});
  const [publishedBusy, setPublishedBusy] = useState<Record<string, boolean>>({});
  const [pushing, setPushing] = useState<Record<string, boolean>>({});
  const [pushError, setPushError] = useState<Record<string, string>>({});

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  useEffect(() => {
    const seed: Record<string, boolean> = {};
    for (const v of videos) seed[v.id] = !!v.published_at;
    setPublishedById(seed);
  }, [videos]);

  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const start = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
  };
  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const loadLibrary = async () => {
    try {
      const res = await fetch('/api/image-library');
      const data = await res.json();
      setLibrary(data.images ?? []);
    } catch (e) { console.error(e); }
    finally { setLibraryLoaded(true); }
  };

  const addChosen = (item: ChosenImage) =>
    setChosen((prev) => (prev.some((c) => c.key === item.key) ? prev : [...prev, item]));
  const removeChosen = (key: string) =>
    setChosen((prev) => {
      const item = prev.find((c) => c.key === key);
      if (item?.file) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((c) => c.key !== key);
    });
  const isChosen = (key: string) => chosen.some((c) => c.key === key);

  // --- Imagn search ---
  const fetchImagnPage = async (terms: string, offset: number): Promise<{ images: ImagnImage[]; total: number }> => {
    const res = await fetch('/api/imagn/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ terms, limit: IMAGN_FETCH_LIMIT, offset }),
    });
    const data = await res.json();
    return { images: data.images ?? [], total: data.total ?? 0 };
  };

  const searchImagn = async (queryOverride?: string) => {
    const q = (queryOverride ?? imagnQuery).trim();
    if (q.length < 2) return;
    const token = ++searchTokenRef.current;
    setImagnSearching(true);
    setImagnLoadingMore(false);
    setImagnResults([]);
    setImagnPage(0);
    try {
      const first = await fetchImagnPage(q, 0);
      if (searchTokenRef.current !== token) return;
      setLastSearched(q); // a search for `q` has completed → enables the empty-state message
      const total = Math.min(first.total || first.images.length, IMAGN_MAX_RESULTS);
      const seen = new Set(first.images.map((i) => i.imageID));
      let all = first.images;
      setImagnResults(all);
      setImagnSearching(false);
      if (all.length < total) {
        setImagnLoadingMore(true);
        for (let offset = IMAGN_FETCH_LIMIT; offset < total && all.length < IMAGN_MAX_RESULTS; offset += IMAGN_FETCH_LIMIT) {
          const next = await fetchImagnPage(q, offset);
          if (searchTokenRef.current !== token) return;
          if (!next.images.length) break;
          const fresh = next.images.filter((i) => !seen.has(i.imageID));
          fresh.forEach((i) => seen.add(i.imageID));
          if (fresh.length === 0) continue;
          all = all.concat(fresh);
          setImagnResults([...all]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (searchTokenRef.current === token) {
        setImagnSearching(false);
        setImagnLoadingMore(false);
      }
    }
  };

  // Click a suggested query chip → open the creator, focus Imagn, run the search.
  const applyImagnQuery = (q: string) => {
    setShowCreate(true);
    if (!libraryLoaded) loadLibrary();
    setSource('imagn');
    setImagnQuery(q);
    searchImagn(q);
    createRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Click "Use as script" → open the creator, drop the text into the script, and
  // set the suggested Imagn queries to the ones that came with this script's
  // match. `key` marks which chip is active.
  const applyScript = (text: string, queries?: string[], key?: string, meta?: ScriptMeta) => {
    setShowCreate(true);
    setScript(text);
    setSelectedQueries((queries ?? []).filter(Boolean));
    setSelectedScriptKey(key ?? null);
    if (meta) { setTeam1(meta.team1); setTeam2(meta.team2); setScore(meta.score); setGoalScorers(meta.goalScorers); }
    createRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // --- Run the two World Cup prompts for the selected date ---
  const runPrompts = async () => {
    if (running) return;
    setRunError(null);
    setRunning(true);
    try {
      const res = await fetch('/api/worldcup/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to run prompts');
      const { run } = await res.json();
      onRefreshRuns();
      if (run?.id) setExpandedRun(run.id); // open the new run in the detailed list
      // Surface the generator + quick script-select right away.
      setShowCreate(true);
      if (!libraryLoaded) loadLibrary();
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Failed to run prompts');
    } finally {
      setRunning(false);
    }
  };

  const deleteRun = async (id: string) => {
    try {
      await fetch(`/api/worldcup/run?id=${id}`, { method: 'DELETE' });
      onRefreshRuns();
    } catch (e) { console.error(e); }
  };

  const addImagn = (img: ImagnImage) => {
    const key = `imagn:${img.imageID}`;
    if (isChosen(key)) { removeChosen(key); return; }
    addChosen({ key, previewUrl: img.previewUrl || img.thumbUrl, imagnId: img.imageID, caption: img.caption });
  };

  const handleUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) =>
      addChosen({ key: `upload:${f.name}:${f.size}:${f.lastModified}`, previewUrl: URL.createObjectURL(f), file: f, caption: f.name }),
    );
  };

  const onDragStart = (i: number) => { dragIndex.current = i; };
  const onDrop = (i: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    setChosen((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
  };

  const previewVoice = async () => {
    if (!script.trim()) return;
    setPreviewing(true);
    try {
      const res = await fetch('/api/tts-timed', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: script }),
      });
      const blob = await res.blob();
      new Audio(URL.createObjectURL(blob)).play();
    } catch (e) { console.error(e); }
    finally { setPreviewing(false); }
  };

  const resetForm = () => {
    chosen.forEach((c) => { if (c.file) URL.revokeObjectURL(c.previewUrl); });
    setChosen([]);
    setScript('');
    setTeam1('');
    setTeam2('');
    setScore('');
    setGoalScorers(EMPTY_SCORERS);
    setShowScore(true);
    setShowFlags(true);
    setShowGoalScorers(true);
    setSelectedScriptKey(null); // also hides the scoreboard until a script is re-picked
    setSelectedQueries([]);
    setVideoSize('9:16');
    setOverlay(DEFAULT_OVERLAY);
    setMusic(WC_DEFAULT_MUSIC);
    setImagnResults([]);
    setImagnQuery('');
    setImagnPage(0);
    setGen({ stage: 'idle', progress: 0 });
  };

  // --- Generate video (tags the row category='worldcup', run_date=date) ---
  const generateVideo = async () => {
    if (chosen.length === 0 || !script.trim()) return;
    try {
      startTimer();
      setGen({ stage: 'preparing', progress: 0, startedAt: Date.now() });

      const imageUrls: string[] = [];
      for (const item of chosen) {
        if (item.s3_url) {
          imageUrls.push(item.s3_url);
        } else if (item.imagnId) {
          const res = await fetch('/api/imagn/download', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageID: item.imagnId, caption: item.caption }),
          });
          if (!res.ok) throw new Error((await res.json()).error || 'Imagn download failed');
          imageUrls.push((await res.json()).image.s3_url);
        } else if (item.file) {
          const form = new FormData();
          form.append('files', item.file);
          const res = await fetch('/api/image-library', { method: 'POST', body: form });
          if (!res.ok) throw new Error((await res.json()).error || 'Image upload failed');
          imageUrls.push((await res.json()).images[0].s3_url);
        }
      }

      // Pre-game scripts (Preview / Betting Line) save a cleared, fully-disabled
      // scoreboard (all toggles off) so the video renders without one; everything
      // else (Recap / Hot Take, or a manually-typed script) keeps the fields as set.
      const scoreboardFields = scriptHasScoreboard(selectedScriptKey)
        ? { team1, team2, score, goalScorers, showScore, showFlags, showGoalScorers }
        : { team1: '', team2: '', score: '', goalScorers: EMPTY_SCORERS, showScore: false, showFlags: false, showGoalScorers: false };

      setGen((p) => ({ ...p, stage: 'audio' }));
      const createRes = await fetch('/api/image-videos', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ script, images: imageUrls, textOverlay: overlay, videoSize, status: 'draft', category: 'worldcup', runDate: date, ...scoreboardFields, ...(music.enabled || music.url ? { musicUrl: music.url, musicEnabled: music.enabled } : {}) }),
      });
      if (!createRes.ok) throw new Error((await createRes.json()).error || 'Failed to save video');
      const { video } = await createRes.json();
      const id: string = video.id;

      const ttsRes = await fetch('/api/tts-timed', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: script, returnUrl: true }),
      });
      if (!ttsRes.ok) throw new Error((await ttsRes.json()).error || 'Audio generation failed');
      const { audioUrl, wordTimingsUrl } = await ttsRes.json();

      setGen((p) => ({ ...p, stage: 'rendering', progress: 0 }));
      const renderRes = await fetch('/api/render-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: id, audioUrl, wordTimingsUrl }),
      });
      if (!renderRes.ok) throw new Error((await renderRes.json()).error || 'Render start failed');
      const { renderId, bucketName } = await renderRes.json();
      onRefresh();

      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`/api/render-images?renderId=${renderId}&bucketName=${bucketName}&videoId=${id}`);
          const progress = await pr.json();
          if (progress.fatalErrorEncountered) {
            clearInterval(poll); stopTimer();
            setGen((p) => ({ ...p, stage: 'error', progress: 0, error: progress.errors?.[0] || 'Render failed', finishedAt: Date.now() }));
            onRefresh();
            return;
          }
          if (progress.done && progress.outputFile) {
            clearInterval(poll); stopTimer();
            setGen((p) => ({ ...p, stage: 'done', progress: 100, videoUrl: progress.outputFile, fileSizeBytes: progress.outputSizeInBytes, finishedAt: Date.now() }));
            onRefresh();
            return;
          }
          setGen((p) => ({ ...p, stage: 'rendering', progress: Math.round((progress.overallProgress || 0) * 100), fileSizeBytes: progress.outputSizeInBytes }));
        } catch {
          clearInterval(poll); stopTimer();
          setGen((p) => ({ ...p, stage: 'error', progress: 0, error: 'Progress check failed', finishedAt: Date.now() }));
        }
      }, 2000);
    } catch (e: unknown) {
      stopTimer();
      setGen((p) => ({ ...p, stage: 'error', progress: 0, error: e instanceof Error ? e.message : 'Unknown error', finishedAt: Date.now() }));
    }
  };

  const deleteVideo = async (id: string) => {
    try { await fetch(`/api/image-videos?id=${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e) { console.error(e); }
  };

  const togglePublished = async (id: string, e: SyntheticEvent) => {
    e.stopPropagation();
    if (publishedBusy[id]) return;
    const next = !publishedById[id];
    setPublishedBusy((p) => ({ ...p, [id]: true }));
    setPublishedById((p) => ({ ...p, [id]: next }));
    try {
      const res = await fetch('/api/image-videos/published', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, published: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setPublishedById((p) => ({ ...p, [id]: !next }));
    } finally {
      setPublishedBusy((p) => ({ ...p, [id]: false }));
    }
  };

  const pushVideo = async (id: string) => {
    if (pushing[id]) return;
    setPushing((p) => ({ ...p, [id]: true }));
    setPushError((p) => { const n = { ...p }; delete n[id]; return n; });
    try {
      const res = await fetch('/api/image-videos/publish', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Push failed');
      setPublishedById((p) => ({ ...p, [id]: !!data.published }));
      onRefresh();
    } catch (e) {
      setPushError((p) => ({ ...p, [id]: e instanceof Error ? e.message : 'Push failed' }));
    } finally {
      setPushing((p) => ({ ...p, [id]: false }));
    }
  };

  const seedEdit = (v: ImageVideo): RowEdit =>
    ({ script: v.script ?? '', videoSize: v.video_size, overlay: v.text_overlay ?? DEFAULT_OVERLAY, music: musicFromVideo(v), scoreboard: scoreboardFromVideo(v) });
  const getEdit = (v: ImageVideo): RowEdit => editState[v.id] ?? seedEdit(v);
  const setEdit = (v: ImageVideo, patch: Partial<RowEdit>) =>
    setEditState((p) => ({ ...p, [v.id]: { ...(p[v.id] ?? seedEdit(v)), ...patch } }));

  const downloadVideo = async (e: ReactMouseEvent, url: string, filename: string, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloading[key]) return;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { console.error(err); }
    finally { setDownloading((p) => ({ ...p, [key]: false })); }
  };

  const fileNameFor = (v: { id: string; script?: string | null }) => {
    const slug = (v.script ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    return `${slug || v.id}.mp4`;
  };

  const loadSize = async (v: ImageVideo) => {
    if (!v.video_url || v.file_size_bytes || sizes[v.id] != null) return;
    try {
      const res = await fetch(v.video_url);
      const len = res.headers.get('content-length');
      res.body?.cancel().catch(() => {});
      if (len) setSizes((p) => ({ ...p, [v.id]: Number(len) }));
    } catch { /* ignore */ }
  };

  const runRender = async (v: ImageVideo, edits?: RowEdit) => {
    if (rowGen[v.id]?.stage === 'rendering') return;
    setRowGen((p) => ({ ...p, [v.id]: { stage: 'rendering', progress: 0 } }));
    try {
      const scriptVal = edits?.script ?? v.script ?? '';
      const videoSizeVal = edits?.videoSize ?? v.video_size;
      const overlayVal = edits?.overlay ?? v.text_overlay;

      if (edits) {
        const up = await fetch('/api/image-videos', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: v.id, script: scriptVal, textOverlay: overlayVal, videoSize: videoSizeVal,
            musicUrl: edits.music.url, musicEnabled: edits.music.enabled,
            team1: edits.scoreboard.team1, team2: edits.scoreboard.team2, score: edits.scoreboard.score,
            goalScorers: edits.scoreboard.goalScorers,
            showScore: edits.scoreboard.showScore, showFlags: edits.scoreboard.showFlags, showGoalScorers: edits.scoreboard.showGoalScorers,
          }),
        });
        if (!up.ok) throw new Error((await up.json()).error || 'Failed to save changes');
      }

      let audioUrl = v.audio_url ?? undefined;
      let wordTimingsUrl = v.word_timings_url ?? undefined;
      if (!audioUrl || (edits && scriptVal !== (v.script ?? ''))) {
        const tts = await fetch('/api/tts-timed', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: scriptVal, returnUrl: true }),
        });
        if (!tts.ok) throw new Error((await tts.json()).error || 'Audio generation failed');
        ({ audioUrl, wordTimingsUrl } = await tts.json());
      }

      const res = await fetch('/api/render-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoId: v.id, audioUrl, wordTimingsUrl }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Render start failed');
      const { renderId, bucketName } = await res.json();
      onRefresh();
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`/api/render-images?renderId=${renderId}&bucketName=${bucketName}&videoId=${v.id}`);
          const progress = await pr.json();
          if (progress.fatalErrorEncountered) {
            clearInterval(poll);
            setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
            onRefresh();
            return;
          }
          if (progress.done && progress.outputFile) {
            clearInterval(poll);
            setRowGen((p) => { const n = { ...p }; delete n[v.id]; return n; });
            setEditState((p) => { const n = { ...p }; delete n[v.id]; return n; });
            onRefresh();
            return;
          }
          setRowGen((p) => ({ ...p, [v.id]: { stage: 'rendering', progress: Math.round((progress.overallProgress || 0) * 100) } }));
        } catch {
          clearInterval(poll);
          setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
          onRefresh();
        }
      }, 2000);
    } catch {
      setRowGen((p) => ({ ...p, [v.id]: { stage: 'error', progress: 0 } }));
    }
  };

  const isGenerating = gen.stage === 'preparing' || gen.stage === 'audio' || gen.stage === 'rendering';

  const imagnTotalPages = Math.max(1, Math.ceil(imagnResults.length / IMAGN_PER_PAGE));
  const imagnPageSafe = Math.min(imagnPage, imagnTotalPages - 1);
  const imagnPageImages = imagnResults.slice(imagnPageSafe * IMAGN_PER_PAGE, imagnPageSafe * IMAGN_PER_PAGE + IMAGN_PER_PAGE);

  // The most recent run drives the quick script-select list at the top of the
  // creator (merged into one row per matchup).
  const latestRun = runs[0];
  const matchups: Matchup[] = latestRun ? mergeMatchups(latestRun) : [];

  // Scoreboard editor shows by default (incl. manual entry); only pre-game scripts
  // (Preview / Betting Line) hide it, since they carry no score yet.
  const showScoreboard = scriptHasScoreboard(selectedScriptKey);

  return (
    <div className="animate-in fade-in duration-500">
      {/* TOP BAR */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-8 py-4 border-b border-white/10 bg-white/[0.03]">
        <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-400 flex items-center gap-2">
          <Trophy size={14} className="text-amber-400" /> World Cup Videos
        </h3>
        <button
          onClick={() => { setShowCreate((v) => !v); if (!libraryLoaded) loadLibrary(); }}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
        >
          <Plus size={14} /> New Video
        </button>
      </div>

      {/* DATE + RUN PROMPTS BAR (replaces the "Enter Player or Topic" research) */}
      <div className="px-6 md:px-8 py-5 border-b border-white/10 bg-white/[0.015]">
        <StepHeader label="World Cup Date" />
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-slate-900/60 border border-white/10 rounded-lg px-2 focus-within:border-white/25 transition-colors">
            <button className="text-slate-500 hover:text-white p-1.5 hover:bg-white/5 rounded" onClick={() => setDate((d) => shiftDate(d, -1))} title="Previous day">
              <ChevronLeft size={16} />
            </button>
            <Calendar size={14} className="text-sky-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="bg-transparent py-2 px-1 text-sm text-white outline-none [color-scheme:dark]"
            />
            <button className="text-slate-500 hover:text-white p-1.5 hover:bg-white/5 rounded" onClick={() => setDate((d) => shiftDate(d, 1))} title="Next day">
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            onClick={runPrompts}
            disabled={running}
            className="bg-amber-500 hover:bg-amber-400 text-black px-5 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
            {running ? 'Running prompts…' : 'Run Prompts'}
          </button>
        </div>
        {runError && <p className="mt-2 text-[10px] text-red-400">{runError}</p>}
      </div>

      {/* CREATE PANEL */}
      {showCreate && (
        <div ref={createRef} className="px-6 md:px-8 py-6 border-b border-white/10 bg-white/[0.015] animate-in slide-in-from-top-2">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <div className="order-2 lg:flex-1 min-w-0 space-y-6">
              {/* QUICK SCRIPT SELECT — latest run, one row per matchup, chips
                  propagate the script (and its image searches) to the generator */}
              {matchups.length > 0 && (
                <div>
                  <StepHeader label={`Pick a Script · ${prettyDate(latestRun!.run_date)}`} />
                  <QuickScriptSelect matchups={matchups} keyPrefix="latest" activeKey={selectedScriptKey} onPick={applyScript} />
                  <p className="mt-2 text-[10px] text-slate-600">
                    Click a script to drop it into the Audio Script below and suggest its image searches. Scroll to “Generated Outputs” to read &amp; compare the full text.
                  </p>
                </div>
              )}

              {/* IMAGE PICKER */}
              <div>
                <StepHeader
                  label="Choose Images"
                  right={
                    <Segmented
                      options={['imagn', 'upload', 'library'] as PickerSource[]}
                      value={source}
                      onChange={(v) => { setSource(v); if (v === 'library' && !libraryLoaded) loadLibrary(); }}
                      labels={{ imagn: 'Imagn', upload: 'Upload', library: 'Library' }}
                    />
                  }
                />

                {source === 'imagn' && (
                  <div>
                    {/* Suggested queries tied to the currently-selected script */}
                    {selectedQueries.length > 0 && (
                      <div className="mb-3 rounded-lg border border-emerald-500/15 bg-emerald-500/[0.04] p-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-emerald-500/80 mb-2">
                          Suggested for the selected script · click to search
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedQueries.map((q, i) => <QueryChip key={i} q={q} onClick={applyImagnQuery} />)}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 mb-3">
                      <div className="flex-1 flex items-center gap-2 bg-slate-900/60 border border-white/10 rounded-lg px-3 focus-within:border-white/25 transition-colors">
                        <Search size={14} className="text-slate-500" />
                        <input
                          value={imagnQuery}
                          onChange={(e) => setImagnQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && searchImagn()}
                          placeholder="Search Imagn (e.g. Lionel Messi World Cup 2026)"
                          className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-slate-600 outline-none"
                        />
                      </div>
                      <button
                        onClick={() => searchImagn()}
                        disabled={imagnSearching || imagnQuery.trim().length < 2}
                        className="bg-white/10 hover:bg-white/20 text-white px-5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-40 flex items-center gap-2 border border-white/10"
                      >
                        {imagnSearching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        Search
                      </button>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {imagnPageImages.map((img) => {
                        const sel = isChosen(`imagn:${img.imageID}`);
                        return (
                          <button
                            key={img.imageID}
                            onClick={() => addImagn(img)}
                            title={img.caption}
                            className={`relative aspect-square rounded-lg overflow-hidden border transition-all group ${
                              sel ? 'border-emerald-500/70 ring-2 ring-emerald-500/25' : 'border-white/10 hover:border-white/40'
                            }`}
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={img.thumbUrl} alt={img.caption} className="w-full h-full object-cover" />
                            {sel ? (
                              <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5"><Check size={10} className="text-white" /></div>
                            ) : (
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Plus size={18} className="text-white" /></div>
                            )}
                          </button>
                        );
                      })}
                      {imagnResults.length === 0 && (
                        <div className="col-span-4 sm:col-span-6 text-center text-slate-600 text-xs py-10 font-mono uppercase tracking-widest px-4">
                          {imagnSearching
                            ? 'Searching…'
                            : lastSearched
                              ? `No images found for “${lastSearched}”`
                              : 'Search to find images'}
                        </div>
                      )}
                    </div>

                    {imagnResults.length > 0 && (
                      <div className="flex items-center justify-between gap-3 mt-3">
                        <span className="text-[10px] font-mono text-slate-600">
                          {imagnResults.length} result{imagnResults.length === 1 ? '' : 's'}
                          {imagnLoadingMore && <span className="text-slate-500"> · loading more…</span>}
                        </span>
                        {imagnTotalPages > 1 && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => setImagnPage((p) => Math.max(0, p - 1))} disabled={imagnPageSafe === 0}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                              <ChevronLeft size={13} /> Prev
                            </button>
                            <span className="text-[10px] font-mono text-slate-400 tabular-nums min-w-[3.5rem] text-center">{imagnPageSafe + 1} / {imagnTotalPages}</span>
                            <button onClick={() => setImagnPage((p) => Math.min(imagnTotalPages - 1, p + 1))} disabled={imagnPageSafe >= imagnTotalPages - 1}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                              Next <ChevronRight size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {source === 'upload' && (
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/10 rounded-xl py-12 cursor-pointer hover:border-white/30 hover:bg-white/[0.02] transition-all">
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleUpload(e.target.files); e.currentTarget.value = ''; }} />
                    <Upload size={24} className="text-slate-500" />
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">Click to add images</span>
                  </label>
                )}

                {source === 'library' && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto">
                    {library.map((img) => {
                      const sel = isChosen(img.s3_url);
                      return (
                        <button
                          key={img.id}
                          onClick={() => (sel ? removeChosen(img.s3_url) : addChosen({ key: img.s3_url, previewUrl: img.s3_url, s3_url: img.s3_url, caption: img.caption ?? undefined }))}
                          title={img.caption ?? ''}
                          className={`relative aspect-square rounded-lg overflow-hidden border transition-all ${
                            sel ? 'border-emerald-500/70 ring-2 ring-emerald-500/25' : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={img.s3_url} alt="" className={`w-full h-full object-cover transition-opacity ${sel ? 'opacity-90' : ''}`} />
                          {sel && <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5"><Check size={10} className="text-white" /></div>}
                        </button>
                      );
                    })}
                    {libraryLoaded && library.length === 0 && (
                      <div className="col-span-4 sm:col-span-6 text-center text-slate-600 text-xs py-10 font-mono uppercase tracking-widest">Library is empty</div>
                    )}
                  </div>
                )}
              </div>

              {/* ORDER */}
              <div>
                <StepHeader label={`Order (${chosen.length})`} right={chosen.length > 0 ? <span className="text-[10px] text-slate-600 normal-case tracking-normal">drag to reorder</span> : undefined} />
                {chosen.length === 0 ? (
                  <div className="text-slate-600 text-xs py-6 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">No images chosen yet</div>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {chosen.map((img, i) => (
                      <div key={`${img.key}-${i}`} draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                        className="relative shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-white/10 cursor-move group">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={img.previewUrl} alt="" className="w-full h-full object-cover pointer-events-none" />
                        <div className="absolute top-1 left-1 bg-black/60 rounded p-0.5"><GripVertical size={12} className="text-white/70" /></div>
                        <button onClick={() => removeChosen(img.key)} className="absolute top-1 right-1 bg-black/60 hover:bg-red-600/90 rounded p-0.5 transition-colors" title="Remove"><X size={12} className="text-white" /></button>
                        <div className="absolute bottom-1 left-1 bg-black/70 rounded px-1 text-[9px] font-mono text-white">{i + 1}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SCOREBOARD — team1 / team2 / score props for the Remotion render.
                  Auto-filled from the picked matchup; flags resolve from the names.
                  Only relevant for post-game scripts (Recap / Hot Take). */}
              {showScoreboard && (
              <div>
                <StepHeader
                  label="Scoreboard"
                  right={
                    <div className="flex flex-wrap items-center gap-1.5">
                      <ToggleChip label="Score" active={showScore} onChange={setShowScore} />
                      <ToggleChip label="Flags" active={showFlags} onChange={setShowFlags} />
                      <ToggleChip label="Goal scorers" active={showGoalScorers} onChange={setShowGoalScorers} />
                    </div>
                  }
                />
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                  <label className="block">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 1</span>
                    <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                      <FlagThumb name={team1} />
                      <input value={team1} onChange={(e) => setTeam1(e.target.value)} placeholder="e.g. Brazil"
                        className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none" />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Score</span>
                    <input value={score} onChange={(e) => setScore(e.target.value)} placeholder="2-1"
                      className="mt-1 block w-full sm:w-24 text-center bg-slate-900/50 border border-white/10 rounded-lg px-2 py-2 text-sm font-mono text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
                  </label>
                  <label className="block">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 2</span>
                    <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                      <FlagThumb name={team2} />
                      <input value={team2} onChange={(e) => setTeam2(e.target.value)} placeholder="e.g. Türkiye"
                        className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none" />
                    </div>
                  </label>
                </div>

                {/* GOAL SCORERS — grouped home/away, editable, sent as the goalScorers prop */}
                <div className="mt-4">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Goal Scorers</span>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ScorerList side="home" teamName={team1} scorers={goalScorers.home}
                      onChange={(home) => setGoalScorers((g) => ({ ...g, home }))} />
                    <ScorerList side="away" teamName={team2} scorers={goalScorers.away}
                      onChange={(away) => setGoalScorers((g) => ({ ...g, away }))} />
                  </div>
                </div>
              </div>
              )}

              {/* SCRIPT */}
              <div>
                <StepHeader
                  label="Audio Script"
                  right={
                    <button onClick={previewVoice} disabled={previewing || !script.trim()}
                      className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all disabled:opacity-40">
                      <Play size={12} className={previewing ? 'animate-spin' : ''} fill="currentColor" />
                      {previewing ? 'Synthesizing…' : 'Preview Voice'}
                    </button>
                  }
                />
                <textarea value={script} onChange={(e) => setScript(e.target.value)}
                  placeholder="Paste the audio script… or click “Use as script” on a generated Preview / Reaction below."
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-4 text-sm text-white placeholder:text-slate-600 focus:border-white/30 outline-none h-28 resize-none transition-all" />
              </div>

              {/* BACKGROUND MUSIC — defaults to the bundled track, on for World Cup */}
              <MusicPicker value={music} onChange={setMusic} builtins={[WORLDCUP_DEFAULT_TRACK]} />

              {/* GENERATE */}
              <div className="max-w-md mx-auto w-full pt-2">
                <button onClick={generateVideo} disabled={isGenerating || chosen.length === 0 || !script.trim()}
                  className="w-full bg-white text-black hover:bg-white/90 py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
                  {isGenerating ? 'Generating…' : 'Generate Video'}
                </button>

                {gen.stage !== 'idle' && gen.stage !== 'done' && (
                  <div className="space-y-2 mt-3">
                    <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${gen.stage === 'error' ? 'bg-red-500' : (gen.stage === 'preparing' || gen.stage === 'audio') ? 'bg-amber-500' : 'bg-sky-500'}`}
                        style={{ width: gen.stage === 'preparing' ? '8%' : gen.stage === 'audio' ? '15%' : gen.stage === 'error' ? '100%' : `${Math.max(20, gen.progress)}%` }} />
                    </div>
                    <p className="text-[10px] font-mono text-center tracking-wide">
                      {gen.stage === 'preparing' && <span className="text-amber-400">Preparing images…</span>}
                      {gen.stage === 'audio' && <span className="text-amber-400">Generating audio…</span>}
                      {gen.stage === 'rendering' && <span className="text-sky-400">Rendering video… {gen.progress}%</span>}
                      {gen.stage === 'error' && <span className="text-red-400">Error: {gen.error}</span>}
                    </p>
                    {gen.stage !== 'error' && (
                      <div className="flex items-center justify-center gap-4 text-[10px] font-mono text-slate-500">
                        <span className="flex items-center gap-1"><Timer size={10} />{formatElapsed(elapsed)}</span>
                        {gen.fileSizeBytes != null && gen.fileSizeBytes > 0 && <span className="flex items-center gap-1"><HardDrive size={10} />{formatFileSize(gen.fileSizeBytes)}</span>}
                      </div>
                    )}
                  </div>
                )}

                {gen.stage === 'done' && gen.videoUrl && (
                  <div className="space-y-2 mt-3">
                    <button onClick={(e) => downloadVideo(e, gen.videoUrl!, fileNameFor({ id: 'worldcup-video', script }), 'dl-gen')} disabled={downloading['dl-gen']}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60">
                      {downloading['dl-gen'] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {downloading['dl-gen'] ? 'Downloading…' : 'Download Video'}
                    </button>
                    <button onClick={resetForm} className="w-full text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest py-2 transition-colors">Start another</button>
                  </div>
                )}
              </div>
            </div>

            {/* OPTIONS COLUMN */}
            <div className="order-1 lg:w-max lg:shrink-0">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
                <div>
                  <span className="text-xs font-semibold text-slate-200 block mb-2">Video size</span>
                  <Segmented options={VIDEO_SIZES} value={videoSize} onChange={setVideoSize} icon={(o) => <RatioBox size={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Text position</span>
                  <Segmented options={POSITIONS} value={overlay.position} onChange={(v) => setOverlay((o) => ({ ...o, position: v }))} icon={(o) => <PositionGlyph position={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Word breaking</span>
                  <Segmented options={WORD_BREAKING} value={overlay.wordBreaking} onChange={(v) => setOverlay((o) => ({ ...o, wordBreaking: v }))} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Font size</span>
                  <Segmented options={FONT_SIZES} value={overlay.size} onChange={(v) => setOverlay((o) => ({ ...o, size: v }))} icon={(o) => <FontGlyph size={o} />} />
                </div>
                <div className="pt-1 border-t border-white/5">
                  <span className="text-xs font-semibold text-slate-200 block mb-2 mt-3">Font color</span>
                  <div className="flex items-center gap-3">
                    <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(overlay.color) ? overlay.color : '#FFFFFF'} onChange={(e) => setOverlay((o) => ({ ...o, color: e.target.value }))}
                      className="w-10 h-8 rounded bg-transparent border border-white/10 cursor-pointer" />
                    <input type="text" value={overlay.color} onChange={(e) => setOverlay((o) => ({ ...o, color: e.target.value }))} spellCheck={false} placeholder="#FFFFFF"
                      className="w-24 bg-slate-900/50 border border-white/10 rounded px-2 py-1.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-white/30 outline-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EXISTING VIDEOS LIST */}
      <div className="hidden md:grid grid-cols-12 px-8 py-3 text-[10px] font-black uppercase tracking-tighter text-slate-500 border-b border-white/5">
        <div className="col-span-1">Preview</div>
        <div className="col-span-3">Script</div>
        <div className="col-span-1">Size</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">Rendered</div>
        <div className="col-span-3 text-right">Action</div>
      </div>

      <div className="divide-y divide-white/5">
        {videos.length === 0 ? (
          <div className="p-16 text-center text-slate-600 font-mono text-xs uppercase tracking-widest flex flex-col items-center gap-3">
            <ImageIcon size={28} className="opacity-40" />
            No videos yet — choose images and a script, then click “Generate Video”
          </div>
        ) : videos.map((v) => {
          const rg = rowGen[v.id];
          const isRendering = rg?.stage === 'rendering' || v.status === 'rendering';
          const failed = rg?.stage === 'error' || (v.status === 'error' && !rg);
          const expanded = expandedRow === v.id;
          const ed = getEdit(v);
          const setSb = (patch: Partial<ScoreboardEdit>) => setEdit(v, { scoreboard: { ...ed.scoreboard, ...patch } });
          const fileSize = v.file_size_bytes ?? sizes[v.id];
          return (
          <div key={v.id} className="group">
            <div className="grid grid-cols-12 px-8 py-4 items-center hover:bg-white/[0.02] transition-colors cursor-pointer"
              onClick={() => { const o = !expanded; setExpandedRow(o ? v.id : null); if (o) loadSize(v); }}>
              <div className="col-span-1">
                <div className="w-12 h-12 rounded-lg overflow-hidden border border-white/10 bg-black">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  {v.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
              </div>
              <div className="col-span-3 pr-4">
                <p className="text-xs text-slate-300 line-clamp-2 italic">{v.script || '— no script —'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-mono text-slate-600">{v.images?.length ?? 0} imgs</span>
                  <PublishBadge published={!!publishedById[v.id]} v={v} />
                </div>
              </div>
              <div className="col-span-1 flex items-center gap-2 text-slate-400">
                <RatioBox size={v.video_size} />
                <span className="text-[11px] font-mono">{v.video_size}</span>
              </div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                  v.status === 'done' ? 'bg-emerald-600/20 text-emerald-400' : isRendering ? 'bg-sky-600/20 text-sky-400' : failed ? 'bg-red-600/20 text-red-400' : 'bg-slate-700/40 text-slate-400'
                }`}>
                  {isRendering ? (rg ? `rendering ${rg.progress}%` : 'rendering') : failed ? 'error' : v.status}
                </span>
              </div>
              <div className="col-span-2 text-[10px] font-mono text-slate-500">
                {v.status === 'done' && v.video_url ? new Date(v.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </div>
              <div className="col-span-3 flex justify-end gap-1 items-center" onClick={(e) => e.stopPropagation()}>
                {failed && (
                  <button onClick={() => runRender(v)} disabled={isRendering} title="Retry render"
                    className="flex items-center gap-1 px-2 py-1.5 rounded text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors text-[10px] font-bold uppercase tracking-wider disabled:opacity-40">
                    <RotateCcw size={13} /> Retry
                  </button>
                )}
                {isRendering && <Loader2 size={14} className="animate-spin text-sky-400" />}
                {v.video_url && (
                  <button onClick={() => pushVideo(v.id)} disabled={pushing[v.id]} title="Push to Instagram + Facebook"
                    className="flex items-center gap-1 px-2 py-1.5 rounded text-fuchsia-400 hover:text-fuchsia-300 hover:bg-fuchsia-500/10 transition-colors text-[10px] font-bold uppercase tracking-wider disabled:opacity-50">
                    {pushing[v.id] ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
                    {pushing[v.id] ? '' : 'Push'}
                  </button>
                )}
                {v.video_url && (
                  <button onClick={() => setViewing(v)} title="View video" className="p-1.5 rounded text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 transition-colors"><Eye size={14} /></button>
                )}
                {v.video_url && (
                  <button onClick={(e) => downloadVideo(e, v.video_url!, fileNameFor(v), `dl-${v.id}`)} disabled={downloading[`dl-${v.id}`]} title="Download video"
                    className="p-1.5 rounded text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                    {downloading[`dl-${v.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                )}
                <label className="flex items-center gap-1.5 cursor-pointer text-[10px] font-black uppercase tracking-tighter pl-1" onClick={(e) => e.stopPropagation()} title="Mark as published (excludes from auto-publish)">
                  <input type="checkbox" checked={!!publishedById[v.id]} disabled={publishedBusy[v.id]} onChange={(e) => togglePublished(v.id, e)} onClick={(e) => e.stopPropagation()} className="accent-emerald-500 w-3.5 h-3.5" />
                  <span className={publishedById[v.id] ? 'text-emerald-500' : 'text-slate-500'}>Published</span>
                </label>
                <button onClick={() => deleteVideo(v.id)} title="Delete" className="p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
                <ChevronDown size={14} className={`text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </div>
            </div>

            {pushError[v.id] && (
              <div className="px-8 pb-3 -mt-2"><p className="text-[10px] text-red-400 text-right">Push failed: {pushError[v.id]}</p></div>
            )}

            {expanded && (
              <div className="px-8 pb-6 pt-2 bg-black/30 border-t border-white/5 animate-in slide-in-from-top-1">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Preview</h5>
                    {v.video_url ? (
                      <video src={v.video_url} controls preload="metadata" className="w-full max-h-[440px] rounded-lg border border-white/10 bg-black object-contain"
                        onLoadedMetadata={(e) => { const d = e.currentTarget.duration; setDurations((p) => (p[v.id] == null ? { ...p, [v.id]: d } : p)); }} />
                    ) : (
                      <div className="w-full aspect-[9/16] rounded-lg border border-dashed border-white/10 bg-black/40 flex items-center justify-center text-center text-slate-600 text-[10px] font-mono uppercase tracking-widest px-2">
                        {isRendering ? 'Rendering…' : 'Not rendered yet'}
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-2 space-y-4">
                    <div>
                      <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Script</h5>
                      <textarea value={ed.script} onChange={(e) => setEdit(v, { script: e.target.value })}
                        className="w-full bg-slate-900/50 border border-white/10 rounded-lg p-3 text-xs text-slate-200 placeholder:text-slate-600 focus:border-white/30 outline-none h-24 resize-none" />
                    </div>

                    {/* SCOREBOARD — editable team1/team2/score + goal scorers, applied on
                        re-generate. Only shown for post-game videos that carry one. */}
                    {videoHasScoreboard(v) && (
                    <div>
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2 min-h-[24px]">
                        <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Scoreboard</h5>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <ToggleChip label="Score" active={ed.scoreboard.showScore} onChange={(val) => setSb({ showScore: val })} />
                          <ToggleChip label="Flags" active={ed.scoreboard.showFlags} onChange={(val) => setSb({ showFlags: val })} />
                          <ToggleChip label="Goal scorers" active={ed.scoreboard.showGoalScorers} onChange={(val) => setSb({ showGoalScorers: val })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                        <label className="block">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 1</span>
                          <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                            <FlagThumb name={ed.scoreboard.team1} />
                            <input value={ed.scoreboard.team1} onChange={(e) => setSb({ team1: e.target.value })} placeholder="e.g. Brazil"
                              className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none" />
                          </div>
                        </label>
                        <label className="block">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Score</span>
                          <input value={ed.scoreboard.score} onChange={(e) => setSb({ score: e.target.value })} placeholder="2-1"
                            className="mt-1 block w-full sm:w-24 text-center bg-slate-900/50 border border-white/10 rounded-lg px-2 py-2 text-sm font-mono text-white placeholder:text-slate-600 focus:border-white/30 outline-none" />
                        </label>
                        <label className="block">
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Team 2</span>
                          <div className="mt-1 flex items-center gap-2 bg-slate-900/50 border border-white/10 rounded-lg px-2 focus-within:border-white/30 transition-colors">
                            <FlagThumb name={ed.scoreboard.team2} />
                            <input value={ed.scoreboard.team2} onChange={(e) => setSb({ team2: e.target.value })} placeholder="e.g. Türkiye"
                              className="flex-1 min-w-0 bg-transparent py-2 text-sm text-white placeholder:text-slate-600 outline-none" />
                          </div>
                        </label>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <ScorerList side="home" teamName={ed.scoreboard.team1} scorers={ed.scoreboard.goalScorers.home}
                          onChange={(home) => setSb({ goalScorers: { ...ed.scoreboard.goalScorers, home } })} />
                        <ScorerList side="away" teamName={ed.scoreboard.team2} scorers={ed.scoreboard.goalScorers.away}
                          onChange={(away) => setSb({ goalScorers: { ...ed.scoreboard.goalScorers, away } })} />
                      </div>
                    </div>
                    )}

                    <div>
                      <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Images ({v.images?.length ?? 0})</h5>
                      <div className="flex flex-wrap gap-2">
                        {(v.images ?? []).map((url, i) => (
                          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/10">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0.5 left-0.5 bg-black/70 rounded px-1 text-[8px] font-mono text-white">{i + 1}</div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1.5">To change images, create a new video.</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h5 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Options</h5>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Video size</span>
                        <Segmented compact options={VIDEO_SIZES} value={ed.videoSize} onChange={(val) => setEdit(v, { videoSize: val })} icon={(o) => <RatioBox size={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Text position</span>
                        <Segmented compact options={POSITIONS} value={ed.overlay.position} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, position: val } })} icon={(o) => <PositionGlyph position={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Word breaking</span>
                        <Segmented compact options={WORD_BREAKING} value={ed.overlay.wordBreaking} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, wordBreaking: val } })} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Font size</span>
                        <Segmented compact options={FONT_SIZES} value={ed.overlay.size} onChange={(val) => setEdit(v, { overlay: { ...ed.overlay, size: val } })} icon={(o) => <FontGlyph size={o} />} />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2">
                        <span className="text-xs font-semibold text-slate-200">Font color</span>
                        <div className="flex items-center gap-2">
                          <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(ed.overlay.color) ? ed.overlay.color : '#FFFFFF'} onChange={(e) => setEdit(v, { overlay: { ...ed.overlay, color: e.target.value } })}
                            className="w-8 h-7 rounded bg-transparent border border-white/10 cursor-pointer" />
                          <input type="text" value={ed.overlay.color} onChange={(e) => setEdit(v, { overlay: { ...ed.overlay, color: e.target.value } })} spellCheck={false}
                            className="w-24 bg-slate-900/50 border border-white/10 rounded px-2 py-1 font-mono text-[11px] text-slate-200 focus:border-white/30 outline-none" />
                        </div>
                      </div>
                    </div>

                    {/* BACKGROUND MUSIC — editable, applied on re-generate */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                      <MusicPicker value={ed.music} onChange={(m) => setEdit(v, { music: m })} builtins={[WORLDCUP_DEFAULT_TRACK]} />
                    </div>

                    <button onClick={() => runRender(v, ed)} disabled={isRendering || !ed.script.trim()}
                      className="w-full mt-1 bg-white text-black hover:bg-white/90 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {isRendering ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} fill="currentColor" />}
                      {isRendering ? `Rendering ${rg?.progress ?? 0}%` : 'Re-generate Video'}
                    </button>
                    {rg?.stage === 'error' && <p className="text-[10px] text-red-400 text-center">Render failed — try again.</p>}

                    <div className="pt-1 text-[10px] text-slate-500 space-y-1">
                      {v.status === 'done' && v.video_url && (
                        <div className="flex items-center gap-4 text-slate-400">
                          {durations[v.id] != null && isFinite(durations[v.id]) ? <span className="flex items-center gap-1" title="Duration"><Clock size={10} />{formatDuration(durations[v.id])}</span> : null}
                          {fileSize ? <span className="flex items-center gap-1" title="File size"><HardDrive size={10} />{formatFileSize(fileSize)}</span> : null}
                        </div>
                      )}
                      <p>Created {new Date(v.created_at).toLocaleString()}</p>
                      {v.published_at && <p className="text-emerald-500">Published {new Date(v.published_at).toLocaleString()}</p>}
                      {v.publish_error && <p className="text-red-400">Publish note: {v.publish_error}</p>}
                      {(v.audio_url || v.word_timings_url) && (
                        <div className="flex gap-3 pt-0.5">
                          {v.audio_url && <a href={v.audio_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={10} /> Audio</a>}
                          {v.word_timings_url && <a href={v.word_timings_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1"><ExternalLink size={10} /> Timings</a>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* GENERATED OUTPUTS (prompt runs) */}
      <div className="px-6 md:px-8 py-5 border-t border-white/10">
        <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-400 mb-4 flex items-center gap-2">
          <FileJson size={14} className="text-sky-400" /> Generated Outputs
          {runs.length > 0 && <span className="text-slate-600 font-mono">({runs.length})</span>}
        </h3>

        {runs.length === 0 ? (
          <div className="text-slate-600 text-xs py-8 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">
            No runs yet — pick a date and hit “Run Prompts”
          </div>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => {
              const open = expandedRun === run.id;
              const view = runView[run.id] ?? 'both';
              const setView = (v: 'both' | 'preview' | 'reaction') => setRunView((p) => ({ ...p, [run.id]: v }));
              const previewMatches = (run.preview_json ?? []) as WorldCupPreviewMatch[];
              const reactionMatches = (run.reaction_json ?? []) as WorldCupReactionMatch[];
              return (
                <div key={run.id} className="border border-white/10 rounded-lg bg-white/[0.02] overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => setExpandedRun(open ? null : run.id)}>
                    <Trophy size={14} className="text-amber-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white">{prettyDate(run.run_date)}</p>
                      <p className="text-[10px] font-mono text-slate-600">{new Date(run.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${run.preview_error ? 'bg-red-600/20 text-red-400' : 'bg-sky-600/20 text-sky-400'}`}>
                        Preview {run.preview_error ? '✕' : previewMatches.length}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${run.reaction_error ? 'bg-red-600/20 text-red-400' : 'bg-amber-600/20 text-amber-400'}`}>
                        Reaction {run.reaction_error ? '✕' : reactionMatches.length}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); deleteRun(run.id); }} title="Delete run"
                        className="p-1.5 rounded text-slate-600 hover:text-red-500 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
                      <ChevronDown size={14} className={`text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {open && (
                    <div className="px-4 pb-5 pt-1 border-t border-white/5 bg-black/20 animate-in slide-in-from-top-1">
                      {/* Quick script-select for this run — pick without scrolling the full text */}
                      {(() => { const mus = mergeMatchups(run); return mus.length > 0 ? (
                        <div className="my-3">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Pick a Script</p>
                          <QuickScriptSelect matchups={mus} keyPrefix={`run:${run.id}`} activeKey={selectedScriptKey} onPick={applyScript} />
                        </div>
                      ) : null; })()}

                      {/* View toggle — full side-by-side text below for reading & comparing */}
                      <div className="flex items-center gap-2 my-3">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-600">Read all</span>
                        <Segmented options={['both', 'preview', 'reaction'] as ('both' | 'preview' | 'reaction')[]} value={view} onChange={setView}
                          labels={{ both: 'Both', preview: 'Preview', reaction: 'Reaction' }} />
                      </div>

                      <div className={`grid gap-5 ${view === 'both' ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
                        {/* PREVIEW */}
                        {view !== 'reaction' && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-[10px] font-black uppercase tracking-widest text-sky-400">Preview</h4>
                              {run.preview_url && <a href={run.preview_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-sky-500 hover:underline flex items-center gap-1"><ExternalLink size={9} /> JSON file</a>}
                            </div>
                            {run.preview_error ? (
                              <p className="text-[11px] text-red-400 bg-red-500/5 border border-red-500/15 rounded-lg p-3">{run.preview_error}</p>
                            ) : previewMatches.length === 0 ? (
                              <p className="text-[11px] text-slate-600 italic">No matches returned.</p>
                            ) : (
                              previewMatches.map((m, i) => (
                                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-bold text-white">{m.teams}</span>
                                    {m.match_time && <span className="text-[10px] font-mono text-slate-400">{m.match_time}</span>}
                                  </div>
                                  <ScriptBlock label="Preview Narrative" text={m.preview_narrative_script} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:preview`)} />
                                  <ScriptBlock label="Betting Line" text={m.betting_line_movement} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:betting`)} />
                                  {(m.image_search_queries ?? []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                                      {(m.image_search_queries ?? []).map((q, qi) => <QueryChip key={qi} q={q} onClick={applyImagnQuery} />)}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                            <details className="group">
                              <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-400 select-none">Raw JSON</summary>
                              <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-black/50 border border-white/10 p-3 text-[10px] leading-relaxed text-slate-400 font-mono">{JSON.stringify(run.preview_json, null, 2)}</pre>
                            </details>
                          </div>
                        )}

                        {/* REACTION */}
                        {view !== 'preview' && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-[10px] font-black uppercase tracking-widest text-amber-400">Reaction</h4>
                              {run.reaction_url && <a href={run.reaction_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-amber-500 hover:underline flex items-center gap-1"><ExternalLink size={9} /> JSON file</a>}
                            </div>
                            {run.reaction_error ? (
                              <p className="text-[11px] text-red-400 bg-red-500/5 border border-red-500/15 rounded-lg p-3">{run.reaction_error}</p>
                            ) : reactionMatches.length === 0 ? (
                              <p className="text-[11px] text-slate-600 italic">No matches returned.</p>
                            ) : (
                              reactionMatches.map((m, i) => (
                                <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2.5">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm font-bold text-white">{m.teams}</span>
                                    {m.verified_score && <span className="text-[11px] font-mono font-bold text-emerald-400">{m.verified_score}</span>}
                                  </div>
                                  <ScriptBlock label="Recap (Factual)" text={m.recap_script_low_temp} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:recap`)} />
                                  <ScriptBlock label="Hot Take" text={m.hot_take_high_temp} onUse={(t) => applyScript(t, m.image_search_queries, `det:${run.id}:${i}:hot`)} />
                                  {(m.image_search_queries ?? []).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                                      {(m.image_search_queries ?? []).map((q, qi) => <QueryChip key={qi} q={q} onClick={applyImagnQuery} />)}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                            <details className="group">
                              <summary className="cursor-pointer text-[9px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-400 select-none">Raw JSON</summary>
                              <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-black/50 border border-white/10 p-3 text-[10px] leading-relaxed text-slate-400 font-mono">{JSON.stringify(run.reaction_json, null, 2)}</pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* VIEW OVERLAY */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in" onClick={() => setViewing(null)}>
          <div className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10">
              <p className="text-xs text-slate-300 italic line-clamp-1">{viewing.script || '— no script —'}</p>
              <button onClick={() => setViewing(null)} title="Close" className="shrink-0 p-1.5 rounded text-slate-500 hover:text-white hover:bg-white/10 transition-colors"><X size={18} /></button>
            </div>
            <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
              {viewing.video_url ? (
                <video src={viewing.video_url} controls autoPlay className="max-h-[70vh] max-w-full" />
              ) : (
                <p className="text-slate-600 text-xs font-mono uppercase tracking-widest py-20">No video available</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10">
              {viewing.video_url && (
                <button onClick={() => pushVideo(viewing.id)} disabled={pushing[viewing.id]}
                  className="flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60">
                  {pushing[viewing.id] ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                  {pushing[viewing.id] ? 'Pushing…' : 'Push'}
                </button>
              )}
              {viewing.video_url && (
                <button onClick={(e) => downloadVideo(e, viewing.video_url!, fileNameFor(viewing), `dl-modal-${viewing.id}`)} disabled={downloading[`dl-modal-${viewing.id}`]}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60">
                  {downloading[`dl-modal-${viewing.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {downloading[`dl-modal-${viewing.id}`] ? 'Downloading…' : 'Download'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
